import { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useMe } from '../me'
import * as buttonTracking from './button'
import * as dialogTracking from './dialog'
import * as dragTracking from './drag'
import * as formTracking from './form'
import * as inputTracking from './input'
import * as menuTracking from './menu'
import { Metadata, PostHogCapture } from './types'
import { usePostHog } from 'posthog-js/react'

type PageData = {
  path: string
  search: string
  hash: string
  url: string
  previousPath: string | undefined
  previousSearch: string | undefined
  previousHash: string | undefined
  previousUrl: string | undefined
}

export const useTracking = () => {
  const postHog = usePostHog()
  const { me } = useMe()
  const location = useLocation()
  const userId = postHog.get_distinct_id()
  const { accountId, siteId, userRole } =
    postHog.get_property('$stored_person_properties') ?? {}

  useEffect(() => {
    const storedPropsChanged =
      accountId !== me?.accountId ||
      siteId !== me?.siteId ||
      userId !== me?.user?.id ||
      userRole !== me?.role

    if (!storedPropsChanged || !me?.user?.id) {
      return
    }
    void postHog.identify(me.user.id, {
      accountId: me.accountId,
      siteId: me.siteId,
      userRole: me.role,
    })
  }, [
    me?.accountId,
    me?.user?.id,
    me?.role,
    me?.siteId,
    accountId,
    siteId,
    userRole,
    postHog,
    userId,
  ])

  const customCapture: PostHogCapture = useCallback(
    (name: string, metadata?: Metadata) => {
      const currentPath = location.pathname
      const search = location.search

      const payload = metadata
        ? {
            ...metadata,
            label: metadata.name,
            path: currentPath,
            search: search,
          }
        : undefined
      return postHog.capture(name, payload)
    },
    [location.pathname, location.search, postHog],
  )
  const trackButtonClick = useMemo(
    () => buttonTracking.trackButtonClick(customCapture),
    [customCapture],
  )

  const trackButtonClickEvent = useMemo(
    () => buttonTracking.trackButtonClickEvent(customCapture),
    [customCapture],
  )

  const trackInput = useMemo(
    () => inputTracking.trackInput(customCapture),
    [customCapture],
  )

  const trackInputFocus = useMemo(
    () => inputTracking.trackInputFocus(customCapture),
    [customCapture],
  )

  const trackInputBlur = useMemo(
    () => inputTracking.trackInputBlur(customCapture),
    [customCapture],
  )

  const trackInputChange = useMemo(
    () => inputTracking.trackInputChange(customCapture),
    [customCapture],
  )

  const trackDialogOpen = useMemo(
    () => dialogTracking.trackDialogOpen(customCapture),
    [customCapture],
  )

  const trackDialogClose = useMemo(
    () => dialogTracking.trackDialogClose(customCapture),
    [customCapture],
  )

  const trackFormSuccess = useMemo(
    () => formTracking.trackFormSuccess(customCapture),
    [customCapture],
  )

  const trackFormError = useMemo(
    () => formTracking.trackFormError(customCapture),
    [customCapture],
  )

  const trackDragStart = useMemo(
    () => dragTracking.trackDragStart(customCapture),
    [customCapture],
  )

  const trackDragEnd = useMemo(
    () => dragTracking.trackDragEnd(customCapture),
    [customCapture],
  )

  const trackMenuOpen = useMemo(
    () => menuTracking.trackMenuOpen(customCapture),
    [customCapture],
  )

  const trackMenuClose = useMemo(
    () => menuTracking.trackMenuClose(customCapture),
    [customCapture],
  )

  const track = useCallback(
    (name: string, metadata: Metadata & { category: string }) => {
      void customCapture(name, {
        ...metadata,
        label: metadata.name,
      })
    },
    [customCapture],
  )

  const page = useCallback(
    (pageData: PageData) => postHog.capture('$pageview', { ...pageData }),
    [postHog],
  )

  return {
    reset: () => postHog.reset(),
    page,
    track,
    trackButtonClick,
    trackButtonClickEvent,
    trackInput,
    trackInputFocus,
    trackInputBlur,
    trackInputChange,
    trackDialogOpen,
    trackDialogClose,
    trackFormSuccess,
    trackFormError,
    trackDragStart,
    trackDragEnd,
    trackMenuOpen,
    trackMenuClose,
  }
}
